export type DataLayerEvents =
  | 'init_ready'
  | 'funnel'
  | 'membership_funnel'
  | 'form_submit'
  | 'form_start'
  | 'membership_funnel_complete'
  | 'voucher_applied'
  | 'free_trial_funnel_complete'
  | 'funnel_error'
  | 'language_switched'
  | 'funnel_receipt'
  | 'voucher_removed';

export type FunnelStepNames =
  | 'funnel_start'
  | 'select_city'
  | 'select_a_club'
  | 'membership_type'
  | 'select_access_level'
  | 'select_rate_bundle'
  | 'optional_paid_module'
  | 'optional_free_module'
  | 'personal_details'
  | 'sms_verification'
  | 'confirmation_page'
  | 'extras'
  | 'member_verification'
  | 'contract_duration';

export type FunnelNames = 'membership' | 'free_trial' | 'membership|free_trial' | 'extension';

/**
 * Distinguishes the two free-trial implementations, which otherwise emit
 * identical events: the `TrialBookingWidget` (a Contentful page widget with its
 * own club picker) and the older multi-step `TrialFlow`. Set on every event of
 * both flows so they can be compared step-by-step in GTM.
 */
export type FunnelVariants = 'booking_widget' | 'trial_flow';

export interface AnalyticsAdapterType<T> {
  track(event: T): void;
}

export enum AnalyticsAdapterEventTypes {
  FUNNEL_START = 'FUNNEL_START',
  DEFAULT_MEMBERSHIP_SELECTED = 'DEFAULT_MEMBERSHIP_SELECTED',
}


export interface DataLayerDataFunnel
  extends Record<
    string,
    string | Record<string, string | ModuleItem[] | number | undefined> | null
  > {
  event: DataLayerEvents;
  funnel_name: FunnelNames;
  /** Only set by the two free-trial flows; `null` elsewhere. */
  funnel_variant: FunnelVariants | null;
  funnel_step_name: FunnelStepNames;
  funnel_step: string | null;
  city: string | null;
  studio_name: string | null;
  studio_id: string | null;
  voucher_code: string | null;
  voucher_type: string | null;
  membership_type: string | null;
  access_type: string | null;
  rate_bundle_term_name: string | null;
  rate_bundle_term_id: string | null;
}

type ModuleItem = {
  item_name: string;
  item_id: string;
  item_category: string;
  item_category2: string;
  price: number;
  quantity: number;
};

type MembershipItem = {
  item_name: string;
  item_id: string;
  coupon: string | undefined;
  discount: number;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_variant: string;
  price: number;
  quantity: number;
  rate_bundle_id: string;
  rate_bundle_term_id: string;
};

export type BecomeAMemberDataLayerData = {
  totalContractValue: number | undefined;
  studio_name: string | undefined;
  studio_id: string | undefined;
  city: string | undefined;
  totalPrice: number | undefined;
  currency: string | undefined;
  membershipItem: MembershipItem;
  paidModules: ModuleItem[];
  freeModules: ModuleItem[];
};

export type ExtensionFlowDataLayerData = {
  studio_id?: string | null;
  membership_type?: string | null;
  access_type?: string | null;
  rate_bundle_term_name?: string | null;
  rate_bundle_term_id?: string | null;
  contract_duration?: string;
  voucher_code?: string | null;
  voucher_type?: string | null;
  city?: string | null;
  studio_name?: string | null;
  billing?: string | null;
  user_data: {
    customer_id: string | undefined;
    customer_number: string | undefined;
  };
}

export type TrialFlowDataLayerData = {
  studio_name: string | undefined;
  studio_id: string | undefined;
  city: string | undefined;
  user_data: {
    email: string | undefined;
    customer_id: string | undefined;
    customer_number: string | undefined;
    phone_number: string | undefined;
  };
};
